import { useState, type CSSProperties } from "react";
import type { WizardController } from "../../../hooks/use-wrapped-api-wizard";
import { useWrappedApi, useWrappedApiMappings, callWrapperTool } from "../../../hooks/use-wrapped-apis";
import { CredentialEditor } from "../CredentialEditor";
import { sampleItem, previewFields } from "../mapping-utils";
import { wizardStyles, MethodBadge } from "./wizard-ui";

interface TestResult {
  status: "idle" | "running" | "ok" | "fail";
  body?: unknown;
  error?: string;
}

/**
 * Step 5 — Test + Done. Set the credential, then run a live GET call per mapped
 * operation and show the materialised entity preview. Failures surface a fix hint
 * and can be skipped (test calls are GET-only — no writes are made during setup).
 */
export function Step5TestDone({ wizard }: { wizard: WizardController }) {
  const detail = useWrappedApi(wizard.slug);
  const mappings = useWrappedApiMappings(wizard.slug);
  const [results, setResults] = useState<Record<string, TestResult>>({});

  const wrapper = detail.data?.wrapper;
  const operations = detail.data?.operations ?? [];
  const mappingByOp = new Map((mappings.data ?? []).map((m) => [m.operation_id, m]));

  // Only GET operations are tested (safety: no writes during setup).
  const testable = wizard.mappableOps.filter((o) => operations.some((d) => d.operation_key === o.operation_id));

  async function runTest(operationId: string) {
    const tool = operations.find((d) => d.operation_key === operationId)?.name;
    if (!tool) return;
    setResults((r) => ({ ...r, [operationId]: { status: "running" } }));
    const res = await callWrapperTool(tool, {});
    setResults((r) => ({
      ...r,
      [operationId]: res.ok ? { status: "ok", body: res.body } : { status: "fail", error: res.error },
    }));
  }

  async function runAll() {
    for (const op of testable) await runTest(op.operation_id);
  }

  return (
    <div>
      <p style={wizardStyles.intro}>
        Your wrapper is live. Set your credential, then run a test call per operation to confirm everything works and
        preview the entities that will materialise.
      </p>

      {wrapper && (
        <div style={{ marginBottom: 16 }}>
          <div style={wizardStyles.sectionTitle}>Your credential</div>
          <CredentialEditor
            slug={wizard.slug}
            authKind={wrapper.auth_kind}
            credentialSet={wrapper.my_credential_set}
            credentialUsername={wrapper.my_credential_username}
            oauthConfigured={wrapper.oauth_configured}
          />
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={wizardStyles.sectionTitle}>Test calls · {testable.length}</div>
        {testable.length > 0 && (
          <button type="button" onClick={runAll} style={runAllBtn}>
            <i className="ti ti-player-play" style={{ fontSize: 12 }} aria-hidden="true" /> Run all
          </button>
        )}
      </div>

      {testable.length === 0 ? (
        <div style={wizardStyles.hint}>No read operations to test.</div>
      ) : (
        testable.map((op) => {
          const result = results[op.operation_id] ?? { status: "idle" as const };
          const mapping = mappingByOp.get(op.operation_id);
          const item = result.status === "ok" ? sampleItem(result.body, mapping?.collection_path ?? null) : null;
          const preview = item && mapping ? previewFields(item, mapping.field_map) : null;
          return (
            <div key={op.operation_id} style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MethodBadge method={op.method} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={opName} title={op.path}>{op.operation_id}</div>
                  {!mapping && <div style={wizardStyles.hint}>No mapping — tests the call only, no materialisation.</div>}
                </div>
                <StatusPill status={result.status} />
                <button type="button" onClick={() => runTest(op.operation_id)} disabled={result.status === "running"} style={runBtn}>
                  {result.status === "running" ? "…" : result.status === "idle" ? "Run test" : "Retry"}
                </button>
              </div>

              {result.status === "fail" && (
                <div style={failBox}>
                  <div style={{ fontWeight: 500, marginBottom: 2 }}>Call failed</div>
                  <div style={{ marginBottom: 4 }}>{result.error}</div>
                  <div style={wizardStyles.hint}>
                    Check your credential above (re-enter / re-authorise), confirm the operation is enabled, then retry.
                    You can also skip this one and finish.
                  </div>
                </div>
              )}

              {result.status === "ok" && (
                <div style={okBox}>
                  {preview && Object.keys(preview).length > 0 ? (
                    <>
                      <div style={previewHead}>Materialised entity preview → {mapping?.target_entity_type_slug}</div>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                          {Object.entries(preview).map(([k, v]) => (
                            <tr key={k}>
                              <td style={previewKey}>{k}</td>
                              <td style={previewVal}>{String(v)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <div style={wizardStyles.hint}>
                      Call succeeded{mapping ? " — but no fields matched; revisit the mapping." : "."}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      <div style={doneNote}>
        <i className="ti ti-circle-check" style={{ fontSize: 14 }} aria-hidden="true" /> Click <strong>Finish</strong> to
        close the wizard. Your wrapper, operations, and mappings are already saved — run <code style={mono}>refresh_tools</code>{" "}
        in your AI host to pick up the new tools.
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: TestResult["status"] }) {
  if (status === "idle") return null;
  const map = {
    running: { label: "running", c: "var(--color-text-tertiary)", bg: "var(--color-background-secondary)" },
    ok: { label: "passed", c: "var(--c-green-800, var(--color-text-secondary))", bg: "var(--c-green-50, var(--color-background-secondary))" },
    fail: { label: "failed", c: "var(--c-red-600, var(--color-text-secondary))", bg: "var(--c-red-50, var(--color-background-secondary))" },
  }[status];
  return (
    <span style={{ fontSize: 10, fontWeight: 500, color: map.c, background: map.bg, borderRadius: "var(--border-radius-sm)", padding: "2px 7px", flexShrink: 0 }}>
      {map.label}
    </span>
  );
}

const card: CSSProperties = { border: "var(--kuvio-border-default)", borderRadius: "var(--border-radius-sm)", padding: "10px 12px", marginBottom: 10 };
const opName: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-mono)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const runBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 12px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  flexShrink: 0,
};
const runAllBtn: CSSProperties = { ...runBtn, display: "inline-flex", alignItems: "center", gap: 4 };
const failBox: CSSProperties = {
  marginTop: 8,
  padding: "8px 10px",
  fontSize: 11,
  color: "var(--c-red-600, var(--color-text-primary))",
  background: "var(--c-red-50, var(--color-background-secondary))",
  borderRadius: "var(--border-radius-sm)",
  lineHeight: 1.5,
};
const okBox: CSSProperties = { marginTop: 8, padding: "8px 10px", background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-sm)" };
const previewHead: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 6,
};
const previewKey: CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  color: "var(--color-text-tertiary)",
  padding: "2px 8px 2px 0",
  verticalAlign: "top",
  whiteSpace: "nowrap",
};
const previewVal: CSSProperties = { fontSize: 11, color: "var(--color-text-primary)", padding: "2px 0", wordBreak: "break-word" };
const doneNote: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 6,
  fontSize: 12,
  color: "var(--color-text-secondary)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "10px 12px",
  marginTop: 12,
  lineHeight: 1.6,
};
const mono: CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 11 };
